import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  btnPrimaryClass,
  btnSecondaryClass,
  formLabelClass,
  formControlClass,
  formGridClass,
  formFullWidthClass,
  alertErrorClass,
  alertInfoClass,
  modalOverlayClass,
  modalPanelClass,
  modalHeaderClass,
  modalBodyClass,
  modalFooterClass,
  modalCloseClass,
  segmentedButtonClass,
} from "../../dashboardStyles";
import {
  GetMyBookingSlotsAction,
  UpdateMyBookingAction,
} from "../../../../store/action";
import { toApiDate } from "./bookingPresentation";
import { formatSlotLabel } from "../../../../utils/dates";
import { notifySaveError } from "../../../../utils/notify";

/**
 * Lets a customer move their own booking, or fix the note and phone on it.
 *
 * Only the time is negotiable here. Service, staff member and business are shown
 * as context but cannot be edited — changing those is a different appointment and
 * belongs on the booking page. The API enforces this regardless.
 */
const EditBookingModal = ({ booking, onClose, onSaved }) => {
  const { t, i18n } = useTranslation();

  const [date, setDate] = useState(() => toApiDate(booking.bookingDateTime));
  const [slot, setSlot] = useState(null);
  const [phone, setPhone] = useState(booking.customerPhone || "");
  const [notes, setNotes] = useState(booking.notes || "");

  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  // The free times could not be loaded. A failed save goes to the snackbar.
  const [error, setError] = useState(null);

  const fetchSlots = useCallback(async () => {
    if (!date) return;
    try {
      setLoadingSlots(true);
      setError(null);
      setSlots(await GetMyBookingSlotsAction(booking.id, date));
    } catch (err) {
      setError(
        err.response?.data?.message || t("Dashboard.MyBookings.errors.slotsFailed"),
      );
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [booking.id, date, t]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  // Changing the day invalidates a slot picked on the previous one.
  useEffect(() => {
    setSlot(null);
  }, [date]);

  const isReschedule = Boolean(slot);

  const handleSave = async () => {
    try {
      setSaving(true);

      const changes = { customerPhone: phone, notes };
      if (slot) {
        changes.bookingDate = date;
        changes.timeSlot = slot;
      }

      const updated = await UpdateMyBookingAction(booking.id, changes);
      onSaved(updated);
    } catch (err) {
      notifySaveError(err, t("Dashboard.MyBookings.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  // Today, in the same UTC frame the bookings are displayed in.
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div
      className={modalOverlayClass}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-booking-title"
    >
      <div className={modalPanelClass}>
        <div className={modalHeaderClass}>
          <h3 id="edit-booking-title" className="m-0 text-base font-semibold text-ink-900">
            {t("Dashboard.MyBookings.edit.title")}
          </h3>
          <button
            type="button"
            className={modalCloseClass}
            onClick={onClose}
            aria-label={t("Dashboard.MyBookings.edit.close")}
          >
            <i className="bi bi-x text-2xl" />
          </button>
        </div>

        <div className={modalBodyClass}>
          {error && (
            <div className={alertErrorClass} role="alert">
              <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <div className={`${alertInfoClass} mb-5`}>
            <i className="bi bi-info-circle mt-0.5 shrink-0" />
            <span>
              {t("Dashboard.MyBookings.edit.context", {
                service: booking.serviceName,
                resource: booking.resourceName,
                business: booking.organizationName,
              })}
            </span>
          </div>

          <div className={formGridClass}>
            <div className={formFullWidthClass}>
              <label className={formLabelClass} htmlFor="edit-booking-date">
                {t("Dashboard.MyBookings.edit.date")}
              </label>
              <input
                id="edit-booking-date"
                type="date"
                className={formControlClass}
                value={date}
                min={today}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            <div className={formFullWidthClass}>
              <p className={formLabelClass}>
                {t("Dashboard.MyBookings.edit.newTime")}
              </p>

              {loadingSlots ? (
                <p className="m-0 text-fluid-sm text-ink-500">
                  {t("Dashboard.MyBookings.edit.loadingSlots")}
                </p>
              ) : slots.length === 0 ? (
                <p className="m-0 text-fluid-sm text-ink-500">
                  {t("Dashboard.MyBookings.edit.noSlots")}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {slots.map((s) => (
                    <button
                      key={s}
                      type="button"
                      aria-pressed={slot === s}
                      onClick={() => setSlot(s)}
                      className={`${segmentedButtonClass(slot === s)} flex-none border border-ink-200`}
                    >
                      {formatSlotLabel(s)}
                    </button>
                  ))}
                </div>
              )}

              <p className="mt-2 mb-0 text-xs text-ink-500">
                {slot
                  ? t("Dashboard.MyBookings.edit.willMove")
                  : t("Dashboard.MyBookings.edit.keepTime")}
              </p>
            </div>

            <div>
              <label className={formLabelClass} htmlFor="edit-booking-phone">
                {t("Dashboard.MyBookings.phone")}
              </label>
              <input
                id="edit-booking-phone"
                type="tel"
                className={formControlClass}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div className={formFullWidthClass}>
              <label className={formLabelClass} htmlFor="edit-booking-notes">
                {t("Dashboard.MyBookings.edit.notes")}
              </label>
              <textarea
                id="edit-booking-notes"
                rows={3}
                className={formControlClass}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          {isReschedule && booking.status?.toLowerCase() === "confirmed" && (
            <p className="mt-4 mb-0 rounded-lg bg-amber-50 p-3 text-fluid-sm text-amber-800">
              <i className="bi bi-exclamation-triangle mr-1.5" aria-hidden="true" />
              {t("Dashboard.MyBookings.edit.reconfirmWarning")}
            </p>
          )}
        </div>

        <div className={modalFooterClass}>
          <button type="button" className={btnSecondaryClass} onClick={onClose}>
            {t("Dashboard.MyBookings.edit.cancel")}
          </button>
          <button
            type="button"
            className={btnPrimaryClass}
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? t("Dashboard.MyBookings.edit.saving")
              : t("Dashboard.MyBookings.edit.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

EditBookingModal.propTypes = {
  booking: PropTypes.shape({
    id: PropTypes.number.isRequired,
    bookingDateTime: PropTypes.string.isRequired,
    serviceName: PropTypes.string,
    resourceName: PropTypes.string,
    organizationName: PropTypes.string,
    customerPhone: PropTypes.string,
    notes: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default EditBookingModal;
